import React, { useState, useEffect } from 'react';
import { X, Mail, CheckCircle, AlertCircle, Clock } from 'lucide-react';

/**
 * Toast notification with optional cancel/undo action.
 * Used by the workflow engine for auto-triggered emails.
 */
const ToastItem = ({ toast, onDismiss, onCancel }) => {
  const [progress, setProgress] = useState(100);
  const [timeLeft, setTimeLeft] = useState(toast.cancelWindowSeconds || 0);

  useEffect(() => {
    if (!toast.cancelWindowSeconds || toast.status !== 'pending') return;

    const totalMs = toast.cancelWindowSeconds * 1000;
    const interval = 50;
    let elapsed = 0;

    const timer = setInterval(() => {
      elapsed += interval;
      const remaining = Math.max(0, 100 - (elapsed / totalMs) * 100);
      setProgress(remaining);
      setTimeLeft(Math.ceil((totalMs - elapsed) / 1000));

      if (elapsed >= totalMs) {
        clearInterval(timer);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [toast.cancelWindowSeconds, toast.status]);

  const icons = {
    pending: <Clock size={18} className="text-amber-500" />,
    sending: <Mail size={18} className="text-blue-500 animate-pulse" />,
    sent: <CheckCircle size={18} className="text-emerald-500" />,
    cancelled: <X size={18} className="text-gray-400" />,
    failed: <AlertCircle size={18} className="text-red-500" />,
  };

  const bgColors = {
    pending: 'bg-white border-amber-200',
    sending: 'bg-white border-blue-200',
    sent: 'bg-white border-emerald-200',
    cancelled: 'bg-gray-50 border-gray-200',
    failed: 'bg-white border-red-200',
  };

  return (
    <div
      className={`relative overflow-hidden rounded-lg border shadow-lg ${bgColors[toast.status]} transition-all duration-300`}
      style={{ width: 380 }}
    >
      {/* Progress bar for cancel window */}
      {toast.status === 'pending' && toast.cancelWindowSeconds > 0 && (
        <div className="absolute top-0 left-0 h-1 bg-amber-400 transition-all duration-50"
          style={{ width: `${progress}%` }}
        />
      )}

      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">{icons[toast.status]}</div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm">
              {toast.status === 'pending' && `Sending in ${timeLeft}s...`}
              {toast.status === 'sending' && 'Sending email...'}
              {toast.status === 'sent' && 'Email sent'}
              {toast.status === 'cancelled' && 'Email cancelled'}
              {toast.status === 'failed' && 'Email failed'}
            </p>
            <p className="text-xs text-gray-600 mt-1 truncate">
              {toast.templateName} → {toast.clientName}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {toast.eventName}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {toast.status === 'pending' && onCancel && (
              <button
                onClick={() => onCancel(toast.id)}
                className="px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition"
              >
                Cancel
              </button>
            )}
            {(toast.status === 'sent' || toast.status === 'cancelled' || toast.status === 'failed') && (
              <button
                onClick={() => onDismiss(toast.id)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded transition"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Toast Container - manages a stack of toast notifications
 */
const ToastContainer = ({ toasts, onDismiss, onCancel }) => {
  if (!toasts || toasts.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 space-y-3">
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onDismiss={onDismiss}
          onCancel={onCancel}
        />
      ))}
    </div>
  );
};

export default ToastContainer;
export { ToastItem };
